import { CheckCircle2, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { useDisruptionPriceBreakdown } from '@/lib/disruptions/use-disruptions';
import { formatPriceMinor, isPriced } from './labels';
import { priceHint } from './disruption-resolution-cockpit';

// Disruption control-center price tab (S7 / NEX-282; the route id stays `cost`).
// airline-money@v2 (commercial-contract M3): every amount is the PRICE the airline
// pays, converted server-side — the headline is the sum of the priced rows. A null
// amount (no price card, or only the unpriceable S4 estimate) reads «No disponible»,
// never 0. `reconciled` is surfaced honestly: when the drill rows don't sum to the
// headline we flag the momentary projector skew rather than hide it. Friendly flight
// labels are primary; case URNs deep-link to case detail.

interface CostTabProps {
  disruptionUrn: string;
  enabled: boolean;
}

export function CostTab({ disruptionUrn, enabled }: CostTabProps) {
  const { t } = useTranslation('disruptions');
  const { data, isLoading, isError } = useDisruptionPriceBreakdown(disruptionUrn, enabled);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-destructive">
          {t('cost.error')}
        </CardContent>
      </Card>
    );
  }

  const priced = isPriced(data.totalPriceMinor);
  const fmt = (minor: number | null | undefined) => formatPriceMinor(minor, data.currency, t);

  return (
    <div className="flex flex-col gap-4" data-testid="cost-tab">
      {/* Headline */}
      <Card>
        <CardContent className="flex flex-wrap items-end justify-between gap-3 p-4">
          <div>
            <p className="text-xs uppercase text-muted-foreground">
              {data.priceSource === 'actual+estimate'
                ? t('cost.headline.actual')
                : t('cost.headline.estimate')}
            </p>
            <p
              className={
                priced
                  ? 'mt-1 text-3xl font-semibold tabular-nums'
                  : 'mt-1 text-xl font-semibold text-muted-foreground'
              }
              data-testid="cost-headline"
            >
              {fmt(data.totalPriceMinor)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground" data-testid="cost-split">
              {priceHint(data)}
            </p>
          </div>
          {/* Nothing to reconcile against a headline that cannot be shown. */}
          {priced ? <ReconciledBadge reconciled={data.reconciled} /> : null}
        </CardContent>
      </Card>

      {/* Per-flight drill */}
      {data.flights.length === 0 ? (
        <EmptyState
          title={t('cost.empty.title')}
          description={t('cost.empty.description')}
        />
      ) : (
        <Card className="overflow-hidden">
          {/* Seven columns: scroll sideways on a phone instead of clipping. */}
          <CardContent className="overflow-x-auto p-0">
            <table className="density-table w-full min-w-[40rem] text-sm" data-testid="cost-table">
              <thead className="border-b border-border bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">{t('cost.table.flight')}</th>
                  <th className="px-3 py-2 text-left">{t('cost.table.airport')}</th>
                  <th className="px-3 py-2 text-right">{t('cost.table.passengers')}</th>
                  <th className="px-3 py-2 text-right">{t('cost.table.booked')}</th>
                  <th className="px-3 py-2 text-right">{t('cost.table.held')}</th>
                  <th className="px-3 py-2 text-right">{t('cost.table.estPending')}</th>
                  <th className="px-3 py-2 text-right">{t('cost.table.price')}</th>
                </tr>
              </thead>
              <tbody>
                {data.flights.map((f) => (
                  <tr key={f.caseUrn} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-2">
                      <Link
                        to={`/cases/${encodeURIComponent(f.caseUrn)}`}
                        className="font-medium text-primary underline-offset-2 hover:underline"
                      >
                        {f.flightLabel}
                      </Link>
                    </td>
                    <td className="px-3 py-2 uppercase text-muted-foreground">{f.airportCode}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {f.passengerCount}
                    </td>
                    {/* An absent split is "not part of this row" (—); a null one is
                        "not available" (the price could not be read). */}
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {f.bookedPriceMinor !== undefined ? fmt(f.bookedPriceMinor) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {f.heldPriceMinor !== undefined ? fmt(f.heldPriceMinor) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {f.estimatedPriceMinor !== undefined ? fmt(f.estimatedPriceMinor) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">
                      {fmt(f.priceMinor)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border bg-muted/30 text-sm font-semibold">
                  <td className="px-3 py-2" colSpan={6}>
                    {t('cost.table.drillTotal')}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums" data-testid="cost-drill-total">
                    {fmt(data.flightsTotalMinor)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ReconciledBadge({ reconciled }: { reconciled: boolean }) {
  const { t } = useTranslation('disruptions');
  return reconciled ? (
    <Badge variant="success" size="md" data-testid="cost-reconciled">
      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
      {t('cost.reconciled')}
    </Badge>
  ) : (
    <Badge
      variant="warning"
      size="md"
      data-testid="cost-unreconciled"
      title={t('cost.reconcilingHint')}
    >
      <TriangleAlert className="h-3.5 w-3.5" aria-hidden />
      {t('cost.reconciling')}
    </Badge>
  );
}
